import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';
import { billingKeys } from './useBilling';

// ─── Transaction Type ────────────────────────────────────────────────────────

export type BillingTransactionType =
  | 'invoice'
  | 'quote'
  | 'credit_note'
  | 'debit_note'
  | 'advance'
  | 'write_off'
  | 'discount';

export const TRANSACTION_TYPE_LABELS: Record<BillingTransactionType, string> = {
  invoice: 'Invoice',
  quote: 'Quote',
  credit_note: 'Credit Note',
  debit_note: 'Debit Note',
  advance: 'Advance',
  write_off: 'Write-Off',
  discount: 'Discount',
};

// ─── Line Item (for invoice / quote) ─────────────────────────────────────────

export interface TransactionLineItem {
  /** Source: 'service' from system lookup, 'job' from client job, 'manual' free-text */
  source: 'service' | 'job' | 'manual';
  serviceTypeCode?: string | null;
  jobId?: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRateId?: string | null;
  /**
   * Stored tax percentage for a line whose `taxRateId` couldn't be resolved
   * (e.g. an invoice converted from a quote). Used for totals and kept on save
   * so the rate isn't reset to 0%. Cleared when the user picks a rate.
   */
  taxRatePercent?: number | null;
  discountType?: 'flat' | 'percentage' | null;
  discountValue?: number | null;
}

// ─── Unified Payload ─────────────────────────────────────────────────────────

export interface BillingTransactionPayload {
  transactionType: BillingTransactionType;
  practiceEntityCode: string;
  clientId: string;

  // Common dates
  transactionDate: string;
  dueDate?: string | null;
  expiryDate?: string | null; // for quotes

  // Invoice / Quote line items
  lineItems?: TransactionLineItem[];

  // Simple amount transactions (write-off, discount, credit note, debit note)
  amount?: number;
  discountType?: 'flat' | 'percentage';

  // References
  invoiceId?: string | null;

  // Metadata
  reason?: string | null;
  notes?: string | null;
  serviceTypeCode?: string | null;
  jobId?: string | null;
  jobReference?: string | null;
  discountDefinitionId?: string | null;

  /**
   * The staff member ("Partner") this transaction is assigned to. Defaults to
   * the creating user on the form, but can be reassigned. Sent as created_by_id.
   */
  assignedToId?: string | null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * Routes the transaction to the correct API endpoint based on transactionType.
 */
export function useCreateBillingTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BillingTransactionPayload) => {
      switch (payload.transactionType) {
        case 'invoice':
          return createInvoice(payload);
        case 'quote':
          return createQuote(payload);
        case 'credit_note':
          return createCreditNote(payload);
        case 'debit_note':
          return createDebitNote(payload);
        case 'advance':
          return createAdvance(payload);
        case 'write_off':
          return createWriteOff(payload);
        case 'discount':
          return createDiscount(payload);
        default:
          throw new Error(`Unknown transaction type: ${payload.transactionType}`);
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate all relevant lists
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      queryClient.invalidateQueries({ queryKey: ['billing', 'write-offs'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'discounts'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-ledger'] });
      queryClient.invalidateQueries({ queryKey: ['debit-notes'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'credit-notes'] });
      queryClient.invalidateQueries({ queryKey: ['billing', 'advances'] });
      if (variables.transactionType === 'quote') {
        queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      }
    },
  });
}

// ─── Per-type API calls ───────────────────────────────────────────────────────

async function createInvoice(payload: BillingTransactionPayload) {
  const response = await apiClient.post<ApiResponse<unknown>>('/billing/invoices', {
    client_id: payload.clientId,
    billing_entity_id: payload.practiceEntityCode,
    issue_date: payload.transactionDate,
    due_date: payload.dueDate || undefined,
    notes: payload.notes || undefined,
    service_type_code: payload.serviceTypeCode || undefined,
    job_id: payload.jobId || null,
    job_reference: payload.jobReference || null,
    discount_definition_id: payload.discountDefinitionId || null,
    created_by_id: payload.assignedToId || undefined,
    line_items: (payload.lineItems || []).map(mapLineItem),
  });
  return response.data.data;
}

async function createQuote(payload: BillingTransactionPayload) {
  const response = await apiClient.post<ApiResponse<unknown>>('/billing/quotes', {
    client_id: payload.clientId,
    practice_entity_id: payload.practiceEntityCode || undefined,
    issue_date: payload.transactionDate,
    expiry_date: payload.expiryDate || undefined,
    notes: payload.notes || undefined,
    discount_definition_id: payload.discountDefinitionId || undefined,
    created_by_id: payload.assignedToId || undefined,
    line_items: (payload.lineItems || []).map(mapLineItem),
  });
  return response.data.data;
}

async function createCreditNote(payload: BillingTransactionPayload) {
  if (!payload.invoiceId) throw new Error('Invoice is required for a credit note');
  if (!payload.amount || payload.amount <= 0)
    throw new Error('Amount is required for a credit note');
  const response = await apiClient.post<ApiResponse<unknown>>(
    `/billing/invoices/${payload.invoiceId}/credit-notes`,
    {
      amount: payload.amount,
      reason: payload.reason || 'Credit note',
      practice_entity_id: payload.practiceEntityCode || undefined,
      transaction_date: payload.transactionDate,
      created_by_id: payload.assignedToId || undefined,
    },
  );
  return response.data.data;
}

async function createDebitNote(payload: BillingTransactionPayload) {
  if (!payload.amount || payload.amount <= 0)
    throw new Error('Amount is required for a debit note');
  const response = await apiClient.post<ApiResponse<unknown>>('/billing/debit-notes', {
    client_id: payload.clientId,
    practice_entity_id: payload.practiceEntityCode || undefined,
    amount: payload.amount,
    reason: payload.reason || '',
    status: 'draft',
    transaction_date: payload.transactionDate,
    created_by_id: payload.assignedToId || undefined,
  });
  return response.data.data;
}

async function createWriteOff(payload: BillingTransactionPayload) {
  if (!payload.invoiceId) throw new Error('Invoice is required for a write-off');
  if (!payload.amount || payload.amount <= 0) throw new Error('Amount is required for a write-off');
  const response = await apiClient.post<ApiResponse<unknown>>('/billing/write-offs', {
    invoice_id: payload.invoiceId,
    amount: payload.amount,
    reason: payload.reason || 'Write-off',
    service_type_code: payload.serviceTypeCode || undefined,
    practice_entity_id: payload.practiceEntityCode || undefined,
    transaction_date: payload.transactionDate,
    created_by_id: payload.assignedToId || undefined,
  });
  return response.data.data;
}

async function createDiscount(payload: BillingTransactionPayload) {
  if (!payload.amount || payload.amount <= 0) throw new Error('Amount is required for a discount');
  const response = await apiClient.post<ApiResponse<unknown>>('/billing/discounts', {
    client_id: payload.clientId,
    invoice_id: payload.invoiceId || undefined,
    amount: payload.amount,
    discount_type: payload.discountType || 'flat',
    reason: payload.reason || undefined,
    service_type_code: payload.serviceTypeCode || undefined,
    transaction_date: payload.transactionDate,
    created_by_id: payload.assignedToId || undefined,
  });
  return response.data.data;
}

async function createAdvance(payload: BillingTransactionPayload) {
  const response = await apiClient.post<ApiResponse<unknown>>('/billing/advances', {
    client_id: payload.clientId,
    practice_entity_id: payload.practiceEntityCode,
    reason: payload.reason || undefined,
    transaction_date: payload.transactionDate,
    due_date: payload.dueDate || undefined,
    job_id: payload.jobId || null,
    job_reference: payload.jobReference || null,
    created_by_id: payload.assignedToId || undefined,
    line_items: (payload.lineItems || []).map(mapLineItem),
  });
  return response.data.data;
}

function mapLineItem(item: TransactionLineItem) {
  return {
    description: item.description,
    quantity: item.quantity,
    unit_price: item.unitPrice,
    tax_rate_id: item.taxRateId || undefined,
    job_id: item.jobId || undefined,
    discount_type: item.discountType || undefined,
    discount_value: item.discountValue || undefined,
  };
}
